import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, filter, map, mergeMap, of, startWith, switchMap, tap } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { BetslipType } from '../../../core/betslip-type';
import { BaseBetslipGroupPick, BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { GroupEnabledPickIds, GroupPickId, isGroupEnabledPickId } from '../../../core/picks/pick-id';
import { BetBuilderUtilService } from '../../bet-builder/services/bet-builder-util.service';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import BetslipPickActions from '../actions';
import {
    addGroup,
    addLegSubscription,
    removeAndReplaceGroup,
    removePicksFromGroup,
    replaceGroup,
    requestAddGroup,
    requestAddPickToGroup,
    requestNavigateToBABTab,
} from '../grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../helpers';
import PickSubscriptionActions from '../pick-subscription-actions';
import { betslipPicksListSelector } from '../selectors';
import { GroupedPicksService } from '../services/grouped-pick/grouped-picks.service';

@Injectable()
export class GroupedPicksEffects {
    constructor(
        private actions$: Actions,
        private store: Store,
        private groupPicksService: GroupedPicksService,
        private betBuilderUtilService: BetBuilderUtilService,
    ) {}

    handleRequestAddGroup$ = createEffect(() => {
        return this.actions$.pipe(
            //check for combo
            ofType(requestAddPickToGroup),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([action, picks]) => this.groupPicksService.validateAvailablePicks(picks, action.groupInfo)),
            mergeMap(([action, picks]) => {
                //can't be null since it was validated during filter ops.
                const existingPlaceholder = this.groupPicksService.getGroupPlaceholder(picks, action.groupInfo)!;
                const actions: Action[] = [];
                //replace with a skeleton only when the group doesn't exist
                if (existingPlaceholder.groupPick.id.toString() !== existingPlaceholder.existingPick.id.toString()) {
                    actions.push(
                        BetslipPickActions.replacePick({
                            newPick: new BetslipUnknownPick(existingPlaceholder.groupPick.id, undefined, [
                                existingPlaceholder.existingPick,
                                new BetslipUnknownPick(action.pickId),
                            ]),
                            pickId: existingPlaceholder.existingPick.id,
                            skipPickTypeStateCleanup: true,
                            skipPickSubscriptionCleanup: true,
                        }),
                    );
                }

                return this.groupPicksService
                    .addPickToGroup(
                        action.isBetBuilderEnabled,
                        action.groupInfo,
                        action.pickId,
                        action.tracking,
                        action.priceId,
                        action.isPriceBoosted,
                        picks,
                    )
                    .pipe(
                        concatMap((groupPick) => {
                            const subscriptionPick = groupPick.getLegs().find((pick) => pick.id.toString() === action.pickId.toString());

                            const removeActions = action.removePick
                                ? [
                                      BetslipPickActions.removePick({
                                          pickId: action.pickId,
                                          skipPickSubscriptionCleanup: true,
                                          skipPickTypeStateCleanup: true,
                                      }),
                                  ]
                                : [];

                            return [
                                addLegSubscription({
                                    pickId: action.pickId,
                                    subscriptionContext: subscriptionPick?.subscriptionContext ?? [],
                                    fromHybridFixture: subscriptionPick?.fromHybridFixture,
                                }),
                                replaceGroup({
                                    newPick: groupPick,
                                    pickId: existingPlaceholder.groupPick.id,
                                }),
                                ...removeActions,
                            ];
                        }),
                        startWith(...actions),
                    );
            }),
        );
    });

    handleRequestAddGroupPick$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(requestAddGroup),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            mergeMap(([action, picks]) =>
                this.groupPicksService
                    .addGroup(action.groupInfo, action.picksIds, action.tracking, action.priceId, action.isPriceBoosted, picks)
                    .pipe(
                        map(({ groupPick, existingPickId }) => ({
                            groupPick,
                            existingPickId,
                            action,
                        })),
                    ),
            ),
            concatMap(({ groupPick, existingPickId, action }) => {
                const getLegDetails = (pickId: string) => {
                    const leg = groupPick.getLegs().find((pick) => pick.id.toString() === pickId.toString());

                    return {
                        subscriptionContext: leg?.subscriptionContext ?? [],
                        fromHybridFixture: leg?.fromHybridFixture,
                    };
                };

                const removeActions = action.removePicks
                    ? action.picksIds.map((pickId) =>
                          BetslipPickActions.removePick({
                              pickId,
                              skipPickSubscriptionCleanup: true,
                              skipPickTypeStateCleanup: true,
                          }),
                      )
                    : [];

                if (existingPickId) {
                    return [
                        ...action.picksIds.map((pickId) =>
                            addLegSubscription({
                                pickId,
                                ...getLegDetails(pickId.toString()),
                            }),
                        ),
                        replaceGroup({
                            newPick: groupPick,
                            pickId: existingPickId,
                            headerTemplate: action.headerTemplate,
                        }),
                        ...removeActions,
                    ];
                }

                return [
                    ...action.picksIds.map((pickId) =>
                        addLegSubscription({
                            pickId,
                            ...getLegDetails(pickId.toString()),
                        }),
                    ),
                    addGroup({ newPick: groupPick, headerTemplate: action.headerTemplate }),
                    ...removeActions,
                ];
            }),
        );
    });

    propagateAddGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(addGroup),
            concatMap(({ newPick, headerTemplate }) => [
                BetslipPickActions.addPick({ pick: newPick }),
                requestNavigateToBABTab({ pick: newPick, headerTemplate }),
            ]),
        );
    });

    handleRemovePicksFromGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(removePicksFromGroup),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([action, picks]) => this.groupPicksService.validateRemovablePick(picks, action.pickIds, action.groupInfo)),
            map(([action, picks]) => ({
                action,
                group: picks.find((t) => t.groupInfo && t.groupInfo.groupId === action.groupInfo.groupId) as BetslipGroupPick,
            })),
            mergeMap(({ action, group }) => {
                const isEntireGroup = group.getLegs().every((l) => action.pickIds.some((inputPickId) => inputPickId.toString() === l.id.toString()));
                if (isEntireGroup) {
                    return of(BetslipPickActions.requestRemovePick({ pickId: group.id }));
                }

                return this.groupPicksService.removePicksFromGroup(group, action.pickIds).pipe(
                    map((pick) => {
                        if (BaseBetslipGroupPick.isPick(pick)) {
                            return replaceGroup({
                                pickId: group.id,
                                newPick: pick,
                            });
                        }

                        return removeAndReplaceGroup({
                            pickId: group.id,
                            newPick: pick,
                        });
                    }),
                );
            }),
        );
    });

    handleAddNewPickToGroupOnPickReplace$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            filter((a) => isGroupEnabledPickId(a.pickId) && !BetslipUnknownPick.isPick(a.newPick) && !!a.newPick.groupInfo?.groupId),
            concatLatestFrom(() => [
                this.store.select(selectIsLinearBetslip),
                this.store.select(betslipCurrentTypeSelector),
                this.store.select(betslipPicksListSelector),
            ]),
            filter(([action, isLinear, currentType, picks]) => {
                return (
                    (isLinear || currentType === BetslipType.Combo) &&
                    picks.some((p) => !p.id.isEqual(action.newPick.id) && p.groupInfo?.groupId === action.newPick.groupInfo!.groupId)
                );
            }),
            concatMap(([action]) => {
                const pick = action.newPick;

                const replace = BetslipPickActions.replacePick({
                    pickId: action.newPick.id,
                    newPick: new BetslipUnknownPick(action.newPick.id, action.newPick.tracking),
                });

                const addToGroup = requestAddPickToGroup({
                    groupInfo: pick.groupInfo!,
                    pickId: pick.id as GroupEnabledPickIds,
                    tracking: pick.tracking,
                    isBetBuilderEnabled: true,
                    removePick: true,
                });

                return [replace, addToGroup];
            }),
        );
    });

    handleAddPicksToNewGroupOnPickReplace$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            filter((a) => BaseBetslipGroupPick.isPick(a.newPick) && !!a.newPick.groupInfo?.groupId),
            concatLatestFrom(() => [
                this.store.select(selectIsLinearBetslip),
                this.store.select(betslipCurrentTypeSelector),
                this.store.select(betslipPicksListSelector),
            ]),
            filter(([action, isLinear, currentType, picks]) => {
                return (
                    (isLinear || currentType === BetslipType.Combo) &&
                    picks.some((p) => !p.id.isEqual(action.newPick.id) && p.groupInfo?.groupId === action.newPick.groupInfo!.groupId)
                );
            }),
            concatMap(([action, , , picksList]) => {
                const existingGroupablePicks = picksList.filter(
                    (p) =>
                        !p.id.isEqual(action.newPick.id) && isGroupEnabledPickId(p.id) && p.groupInfo?.groupId === action.newPick.groupInfo!.groupId,
                );

                if (existingGroupablePicks.length === 0) {
                    return [];
                }

                const replace = existingGroupablePicks.map((pick) =>
                    BetslipPickActions.replacePick({
                        pickId: pick.id,
                        newPick: new BetslipUnknownPick(pick.id, pick.tracking),
                    }),
                );

                const addGroupAction = requestAddGroup({
                    groupInfo: action.newPick.groupInfo!,
                    tracking: existingGroupablePicks[0].tracking,
                    picksIds: existingGroupablePicks.map((p) => p.id as GroupEnabledPickIds),
                    removePicks: true,
                });

                return [...replace, addGroupAction];
            }),
        );
    });

    propagateReplaceGroup$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(replaceGroup, removeAndReplaceGroup),
            concatMap((action) => [
                BetslipPickActions.replacePick({ ...action, skipPickSubscriptionCleanup: true, skipPickTypeStateCleanup: true }),
                requestNavigateToBABTab({ pick: action.newPick, headerTemplate: action.headerTemplate }),
            ]),
        );
    });

    // Fetch latest groupPick price info and replace the current pick with latest state
    // Used in subscription expiration scenarios
    handleUpdateGroupPickSubscription$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.refreshGroupPick),
            switchMap(({ pick: oldPick }) => this.groupPicksService.getGroupPick(oldPick).pipe(map((newPick) => ({ oldPick, newPick })))),
            map(({ oldPick, newPick }) => replaceGroup({ pickId: oldPick.id, newPick })),
        );
    });

    // Push subscription management for group picks. Group picks are not handled as part of regular picks push effects
    handleReplaceGroupPushSubscriptionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(replaceGroup),
            concatMap(function* (action) {
                if (GroupPickId.isId(action.pickId)) {
                    yield PickSubscriptionActions.unsubscribeFromPick({ pickId: action.pickId });
                }

                yield PickSubscriptionActions.subscribeToPick({
                    pick: {
                        pickId: action.newPick.id,
                        context: action.newPick.subscriptionContext,
                        fromHybridFixture: action.newPick.fromHybridFixture,
                    },
                });
            }),
        );
    });

    // Group pick subscription clean-up when a group gets replaced with a regular pick
    // Used in betslip tabs switching scenarios, ei. Combo to Single/System
    handleReplaceRegularPickPushUnsubscriptionEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.replacePick),
            filter(({ pickId, newPick }) => GroupPickId.isId(pickId) && !BaseBetslipGroupPick.isPick(newPick)), // we unsubscribe from the group when we replace it with a regular pick
            map((action) => PickSubscriptionActions.unsubscribeFromPick({ pickId: action.pickId })),
        );
    });

    handleRequestNavigateToBABTab$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(requestNavigateToBABTab),
                filter(({ headerTemplate }) => !!headerTemplate),
                tap(({ pick, headerTemplate }) => this.betBuilderUtilService.navigateToBABTab(pick as BetslipGroupPick, headerTemplate!)),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
